import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getInsight } from "../lib/api";
import { Reveal } from "../components/Reveal";

export default function InsightDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    getInsight(slug).then(setArticle).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-[72px]" data-testid="insight-not-found">
        <div className="text-center">
          <p className="font-serif text-4xl text-ivory">This article isn't in the library.</p>
          <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory/80 hover:text-ivory">
            <ArrowLeft className="h-4 w-4" /> All insights
          </Link>
        </div>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="pt-[72px]" data-testid="insight-loading">
        <div className="mx-auto max-w-4xl animate-pulse px-6 py-20">
          <div className="h-10 w-2/3 bg-ivory/10" />
          <div className="mt-8 aspect-[16/8] bg-ivory/10" />
        </div>
      </main>
    );
  }

  return (
    <main className="pt-[72px]" data-testid="insight-detail-page">
      <article className="mx-auto max-w-4xl px-6 py-14 lg:py-20">
        <Link to="/insights" data-testid="insight-back-link" className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-ivory">
          <ArrowLeft className="h-4 w-4" /> All insights
        </Link>

        <Reveal>
          <p className="eyebrow mt-10 text-ivory/60">{article.category}</p>
          <h1 className="mt-5 font-serif text-4xl leading-tight text-ivory sm:text-5xl" data-testid="insight-title">{article.title}</h1>
          <div className="mt-6 border border-ivory/25 bg-white/5 p-5" data-testid="insight-placeholder-banner">
            <p className="text-xs leading-relaxed text-ivory/65">
              <strong className="text-ivory">Editorial placeholder.</strong> This article demonstrates layout only.
              It carries no verified market data, transaction figures or publication date. Production articles will be
              written and fact-checked by Andy's team before release.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 aspect-[16/8] overflow-hidden">
            <img src={article.image} alt={article.title} className="h-full w-full object-cover" data-testid="insight-hero-image" />
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-10 space-y-6" data-testid="insight-body">
            {article.body.map((p, i) => (
              <p key={i} className={`${i === 0 ? "font-serif text-2xl leading-relaxed text-ivory" : "text-base leading-relaxed text-ivory/70"}`}>{p}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 border border-ivory/10 bg-[#242424] p-10 text-center">
            <p className="font-serif text-3xl text-ivory">Questions on the landed market?</p>
            <p className="mx-auto mt-3 max-w-md text-sm text-ivory/60">Andy reads every brief personally. Start a non-obligatory conversation.</p>
            <button
              type="button"
              onClick={() => navigate("/#contact")}
              data-testid="insight-cta-contact"
              className="mt-6 bg-brand px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-brand-dark"
            >
              Speak to Andy
            </button>
          </div>
        </Reveal>
      </article>
    </main>
  );
}
